const INITIAL_DELAY_MS = 2000;
const MAX_DELAY_MS = 60_000;
const JITTER_RATIO = 0.2;

export interface BackoffConnection {
  close: () => void;
}

export interface BackoffEventSourceHandle {
  close: () => void;
}

export interface BackoffConnectCallbacks {
  readonly onConnected: () => void;
  readonly onDisconnected: () => void;
}

export interface BackoffEventSourceOptions {
  readonly connect: (
    callbacks: BackoffConnectCallbacks,
  ) => Promise<BackoffConnection>;
  readonly logLabel: string;
}

function computeNextDelay(currentDelay: number): number {
  const doubled = Math.min(currentDelay * 2, MAX_DELAY_MS);
  const jitter = doubled * JITTER_RATIO * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(doubled + jitter));
}

export function createBackoffEventSource(
  options: BackoffEventSourceOptions,
): BackoffEventSourceHandle {
  let currentDelayMs = INITIAL_DELAY_MS;
  let connection: BackoffConnection | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let isDown = false;
  let cancelled = false;

  const reportDown = () => {
    if (!isDown) {
      isDown = true;
      console.warn(
        `[${options.logLabel}] connection lost; retrying with backoff`,
      );
    }
  };

  const reportUp = () => {
    if (isDown) {
      console.info(`[${options.logLabel}] connection restored`);
    }
    isDown = false;
    currentDelayMs = INITIAL_DELAY_MS;
  };

  const closeConnection = () => {
    if (connection) {
      try {
        connection.close();
      } catch {
        // Ignore errors during close
      }
      connection = undefined;
    }
  };

  const scheduleRetry = () => {
    if (cancelled || retryTimer != null) {
      return;
    }
    const delay = currentDelayMs;
    currentDelayMs = computeNextDelay(currentDelayMs);
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      void attempt();
    }, delay);
  };

  const attempt = async () => {
    if (cancelled) {
      return;
    }
    closeConnection();
    try {
      const next = await options.connect({
        onConnected: () => {
          if (cancelled) {
            return;
          }
          reportUp();
        },
        onDisconnected: () => {
          if (cancelled) {
            return;
          }
          reportDown();
          closeConnection();
          scheduleRetry();
        },
      });
      if (cancelled) {
        try {
          next.close();
        } catch {
          // Ignore errors during close
        }
        return;
      }
      connection = next;
    } catch {
      if (cancelled) {
        return;
      }
      reportDown();
      scheduleRetry();
    }
  };

  void attempt();

  return {
    close: () => {
      cancelled = true;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }
      closeConnection();
    },
  };
}
